import { parseDate } from '@internationalized/date';
import { ApiClient } from 'lib/api/ApiClient.types';
import { parseWithDecoratedError } from 'v4/entities/common/parseWithDecoratedError';
import { z } from 'zod';
import type { DashboardApi, DashboardProduction } from './dashboard.api';

const API_URL_V1 = import.meta.env.VITE_API_URL;

/**
 * The v1 read contract (`GET /web/v1/workspaces/:schema/dashboard`). Field
 * names are the database's own, kept that way by the backend's rule.
 */
const dashboardProduction$ = z.object({
  id: z.number(),
  title: z.string().nullable(),
  // Text that parses as JSON arrives parsed: "2026" as a number, rich text
  // as an object. Plain values are shown, rich text is not.
  code: z.unknown(),
  enterprise_workspace_id: z.number().nullable(),
  enterprise_team_id: z.number().nullable(),
  production_date: z.object({
    start_date: z.string().date(),
    end_date: z.string().date().nullable()
  }),
  slots_total: z.number(),
  slots_booked: z.number(),
  open_slot_names: z.array(z.string().nullable()),
  crew: z.array(
    z.object({
      contact_id: z.number(),
      full_name: z.string(),
      profile_picture: z.string().nullable()
    })
  ),
  callsheet: z
    .object({ id: z.number(), status: z.string().nullable() })
    .nullable()
});

const dashboard$ = z.object({
  productions: z.array(dashboardProduction$),
  productions_total: z.number(),
  pencilled_bookings_total: z.number()
});

function dashboardProductionFromHttp(
  raw: z.infer<typeof dashboardProduction$>
): DashboardProduction {
  const start = parseDate(raw.production_date.start_date);
  const end = raw.production_date.end_date
    ? parseDate(raw.production_date.end_date)
    : start;
  return {
    id: raw.id,
    title: raw.title,
    code: codeFromHttp(raw.code),
    enterpriseWorkspaceId: raw.enterprise_workspace_id,
    enterpriseTeamId: raw.enterprise_team_id,
    dates: { start, end: end.compare(start) > 0 ? end : start },
    slotsBooked: raw.slots_booked,
    slotsTotal: raw.slots_total,
    openRoles: raw.open_slot_names,
    crew: raw.crew.map(person => ({
      contactId: person.contact_id,
      name: person.full_name,
      pictureUrl: person.profile_picture
    })),
    callsheet: raw.callsheet
  };
}

function codeFromHttp(code: unknown) {
  switch (typeof code) {
    case 'string':
      return code.length > 0 ? code : null;
    case 'number':
    case 'boolean':
      return String(code);
    default:
      return null;
  }
}

export function createDashboardApi(client: ApiClient): DashboardApi {
  return {
    async get(input, signal) {
      const response = await client.get(
        `${API_URL_V1}/workspaces/${input.workspace}/dashboard`,
        {
          token: true,
          signal,
          params: {
            from: input.dateRange.from.toString(),
            to: input.dateRange.to.toString(),
            ...(input.enterpriseWorkspaceId != null
              ? { enterprise_workspace_id: input.enterpriseWorkspaceId }
              : {}),
            ...(input.enterpriseTeamId != null
              ? { enterprise_team_id: input.enterpriseTeamId }
              : {}),
            ...(input.userId ? { user_id: input.userId } : {})
          }
        }
      );
      const parsed = parseWithDecoratedError(dashboard$, response);
      return {
        productions: parsed.productions.map(dashboardProductionFromHttp),
        productionsTotal: parsed.productions_total,
        pencilledBookingCount: parsed.pencilled_bookings_total
      };
    }
  };
}
